/**
 * Фильтр «дата первой поставки на площадку» раздела «Товары»: запись, разбор,
 * слова. Спека — `docs/specs/2026-11-04-products-period-firstsupply-design.md`,
 * серверный разбор — `app/core/modules/catalog/first_supply.py`.
 *
 * ЗАПИСЬ — ОДНА СТРОКА `wb:2026-10-06:2026-11-04`, как её ждёт ручка (`fs=`):
 * граница может быть пустой (`ozon:2026-09-01:`), обе пустыми — нельзя. Строка, а
 * не объект: она едет в адрес, в хранилище и в ключ состояния без перевода.
 */

import { isValidDate, lastDays } from "@/lib/dates";

export type FsPlatform = "wb" | "ozon";

export const FS_PLATFORM_LABEL: Record<FsPlatform, string> = { wb: "WB", ozon: "Ozon" };

export type FirstSupply = { platform: FsPlatform; from: string; to: string };

const isPlatform = (value: string | undefined): value is FsPlatform => value === "wb" || value === "ozon";

/** Разбор строго по правилам ручки; всё прочее — `null`, то есть «отбора нет». */
export function parseFs(raw: string | null | undefined): FirstSupply | null {
  if (typeof raw !== "string") return null;
  const [platform, from = "", to = "", ...лишнее] = raw.split(":");
  if (!isPlatform(platform) || лишнее.length > 0) return null;
  if ((from !== "" && !isValidDate(from)) || (to !== "" && !isValidDate(to))) return null;
  if (from === "" && to === "") return null;
  if (from !== "" && to !== "" && from > to) return null;
  return { platform, from, to };
}

export function formatFs(f: FirstSupply): string {
  return `${f.platform}:${f.from}:${f.to}`;
}

/** Канонический вид или `null`: так мусор адреса и хранилища молча берёт умолчание. */
export function cleanFs(raw: string | null | undefined): string | null {
  const got = parseFs(raw);
  return got ? formatFs(got) : null;
}

/** Выбор площадки: окно по умолчанию — последние 30 дней, то есть «товары, что
 *  впервые поехали за месяц». Пустая граница не годится (ручка её не примет), а
 *  открытое окно «с начала времён» отбором не является. */
export function startFs(platform: FsPlatform): string {
  const [from, to] = lastDays(30);
  return formatFs({ platform, from, to });
}

const короткая = (value: string): string => `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(2, 4)}`;

/** Слово отбора для подписи и причины пустого набора: «первая поставка на WB 06.10.26–04.11.26». */
export function fsWord(raw: string | null): string | null {
  const f = parseFs(raw);
  if (!f) return null;
  const даты = !f.to ? `с ${короткая(f.from)}` : !f.from ? `по ${короткая(f.to)}` : `${короткая(f.from)}–${короткая(f.to)}`;
  return `первая поставка на ${FS_PLATFORM_LABEL[f.platform]} ${даты}`;
}
